import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { notificationsApi } from '../../lib/api';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Bell, Mail, Check, CheckCheck, User, Briefcase, AlertCircle, Loader2, Settings2, Quote, Sun, Moon, Monitor, Plus, UserPlus, ClipboardList, Send, FileSignature, X, HelpCircle, Search } from 'lucide-react';
import { cn } from '../../lib/utils';
import NotifyCenterBridge from '../notify/NotifyCenterBridge';
import NotificationPanelShell from '../notify/NotificationPanelShell';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Slider } from '../ui/slider';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import MailNotifyBridge, { MailNotificationRow } from '../mail/MailNotifyBridge';
import { openCommandPalette } from '../../lib/commandRegistry';
import { previewText, safeText } from '../../lib/renderSafe';
import { keepHeld } from '../../lib/snoozeHold';

const Header = ({ title, subtitle, children }) => {
  const { user } = useAuth();
  const { theme, resolvedTheme, setTheme, cycleTheme, isDark } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showDropdown, setShowDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);
  const pollInterval = useRef(null);
  const lastActivityTimestamp = useRef(null);
  
  // Quick Actions state
  const [showQuickActions, setShowQuickActions] = useState(false);
  const quickActionsRef = useRef(null);
  
  // Check if user has agency permission (admin/super_admin)
  const hasAgencyPermission = user?.role === 'admin' || user?.role === 'super_admin' || user?.permissions?.agency_workspace;

  // Marquee state
  const [marqueeModalOpen, setMarqueeModalOpen] = useState(false);
  const [marqueeQuotes, setMarqueeQuotes] = useState(() => {
    const saved = localStorage.getItem('marquee_quotes');
    return saved ? JSON.parse(saved) : ['Welcome to DCS ATS', 'Focus on results', 'Close deals faster'];
  });
  const [marqueeSettings, setMarqueeSettings] = useState(() => {
    const saved = localStorage.getItem('marquee_settings');
    return saved ? JSON.parse(saved) : { speed: 50, fontSize: 14, fontStyle: 'bold' };
  });
  const [editQuotes, setEditQuotes] = useState(['', '', '', '', '', '']);
  const [currentQuoteIndex, setCurrentQuoteIndex] = useState(0);

  useEffect(() => {
    loadNotifications();
    pollInterval.current = setInterval(loadNotifications, 30000);
    return () => {
      if (pollInterval.current) clearInterval(pollInterval.current);
    };
  }, []);

  // Rotate quotes
  useEffect(() => {
    if (marqueeQuotes.length > 1) {
      const interval = setInterval(() => {
        setCurrentQuoteIndex(prev => (prev + 1) % marqueeQuotes.length);
      }, Math.max(3000, 8000 - (marqueeSettings.speed * 50)));
      return () => clearInterval(interval);
    }
  }, [marqueeQuotes, marqueeSettings.speed]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadNotifications = async () => {
    try {
      const response = await notificationsApi.list({ limit: 20 });
      setNotifications(response.data.notifications);
      setUnreadCount(response.data.unread_count);
      
      // Also load recent activity logs
      const token = localStorage.getItem('ats_token');
      if (token) {
        const activityRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/activity-logs/recent?limit=5`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (activityRes.ok) {
          const activityData = await activityRes.json();
          // Add activity logs as notifications
          const activityNotifs = (activityData.logs || []).map(log => ({
            id: `activity_${log.id}`,
            type: 'activity',
            title: log.action.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
            message: log.details || `${log.entity_type}: ${log.entity_id || ''}`,
            created_at: log.created_at,
            read: false,
            entity_type: log.entity_type,
            entity_id: log.entity_id,
            user_name: log.user_name
          }));
          
          // Show toast for new activity (only if not from current user and after initial load)
          if (lastActivityTimestamp.current && activityData.logs?.length > 0) {
            const newestLog = activityData.logs[0];
            const newestTime = new Date(newestLog.created_at).getTime();
            if (newestTime > lastActivityTimestamp.current && newestLog.user_name !== user?.full_name) {
              toast(`${newestLog.user_name}: ${newestLog.action.replace(/_/g, ' ')}`, {
                description: newestLog.entity_name || newestLog.details || newestLog.entity_type,
                duration: 4000,
                position: 'bottom-right',
              });
            }
          }
          
          // Update timestamp
          if (activityData.logs?.length > 0) {
            lastActivityTimestamp.current = new Date(activityData.logs[0].created_at).getTime();
          }
          
          // Combine and sort by date
          const combined = [...response.data.notifications, ...activityNotifs]
            .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
            .slice(0, 15);
          // keepHeld: a row whose snooze menu is open survives the
          // refresh, same object, so React does not remount it.
          setNotifications((prev) => keepHeld(prev, combined, 15));
        }
      }
    } catch (error) {
      console.error('Failed to load notifications');
    }
  };

  const handleMarkRead = async (notificationId) => {
    try {
      await notificationsApi.markRead(notificationId);
      setNotifications(prev =>
        prev.map(n => n.id === notificationId ? { ...n, read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Failed to mark as read');
    }
  };

  const handleNotificationClick = async (notification) => {
    // mailbox-v3: a mail notification opens the conversation in the Mailbox.
    if (notification.entity_type === 'mail') {
      if (!notification.read) handleMarkRead(notification.id);
      setShowDropdown(false);
      const mailParams = new URLSearchParams();
      if (notification.thread_key) mailParams.set('thread', notification.thread_key);
      if (notification.mailbox) mailParams.set('mailbox', notification.mailbox);
      navigate(`/ats/inbox${mailParams.toString() ? `?${mailParams.toString()}` : ''}`);
      return;
    }
    if (!notification.read && !notification.id.startsWith('activity_')) {
      await handleMarkRead(notification.id);
    }
    setShowDropdown(false);
    
    // Handle activity logs differently - go to activity page
    if (notification.id.startsWith('activity_') || notification.type === 'activity') {
      navigate('/ats/activity');
      return;
    }
    
    if (notification.entity_type && notification.entity_id) {
      switch (notification.entity_type) {
        case 'candidate':
          navigate(`/ats/candidates/${notification.entity_id}`);
          break;
        case 'job':
          navigate(`/ats/jobs/${notification.entity_id}`);
          break;
        case 'match':
          navigate(`/ats/matches/${notification.entity_id}`);
          break;
        case 'portal_auth':
          navigate('/ats/activity');
          break;
        default:
          navigate('/ats/activity');
          break;
      }
    }
  };

  const handleMarkAllRead = async () => {
    setLoading(true);
    try {
      await notificationsApi.markAllRead();
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Failed to mark all as read');
    } finally {
      setLoading(false);
    }
  };

  const openMarqueeModal = () => {
    const filled = [...marqueeQuotes];
    while (filled.length < 6) filled.push('');
    setEditQuotes(filled);
    setMarqueeModalOpen(true);
  };

  const saveMarqueeSettings = () => {
    const validQuotes = editQuotes.filter(q => q.trim());
    const newQuotes = validQuotes.length > 0 ? validQuotes : ['Welcome to DCS ATS'];
    setMarqueeQuotes(newQuotes);
    localStorage.setItem('marquee_quotes', JSON.stringify(newQuotes));
    localStorage.setItem('marquee_settings', JSON.stringify(marqueeSettings));
    setMarqueeModalOpen(false);
  };

  // Quick Actions keyboard shortcut (Q)
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Don't trigger if user is typing in an input/textarea
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.isContentEditable) {
        return;
      }
      if (e.key === 'q' || e.key === 'Q') {
        e.preventDefault();
        openCommandPalette();
      }
      if (e.key === 'Escape') {
        setShowQuickActions(false);
      }
    };
    
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Click outside handler for Quick Actions
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (quickActionsRef.current && !quickActionsRef.current.contains(e.target)) {
        setShowQuickActions(false);
      }
    };
    
    if (showQuickActions) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showQuickActions]);


  const getNotificationIcon = (type) => {
    switch (type) {
      case 'mail_in': case 'mail_out': case 'mail_reminder': case 'mail_snoozed_back':
        return <Mail className="w-4 h-4 text-amber-600" />;  // mailbox-v3
      case 'mail_failed':
        return <AlertCircle className="w-4 h-4 text-red-500" />;
      case 'cv_parsed':
        return <User className="w-4 h-4 text-emerald-500" />;
      case 'cv_parse_failed':
        return <AlertCircle className="w-4 h-4 text-red-500" />;
      case 'jd_parsed':
        return <Briefcase className="w-4 h-4 text-blue-500" />;
      default:
        return <Bell className="w-4 h-4 text-slate-400" />;
    }
  };

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const fontStyleClasses = {
    'normal': '',
    'italic': 'italic',
    'bold': 'font-bold',
    'bold-italic': 'font-bold italic'
  };

  return (
    <>
      <NotifyCenterBridge />
      <header className="header px-6 py-4 max-md:px-4">
        <div className="flex items-center justify-between max-md:flex-col max-md:items-stretch max-md:gap-3">
          <div className="max-md:min-w-0">
            <h1 className="text-2xl font-semibold tracking-tight text-slate-900">{title}</h1>
            {subtitle && <p className="text-sm text-slate-500 mt-0.5">{subtitle}</p>}
          </div>
          
          <div className="flex items-center gap-4 max-lg:flex-wrap max-lg:justify-end max-lg:gap-y-2 max-md:w-full max-md:flex-wrap max-md:justify-start max-md:gap-2">
            {/* mobile-round1: the page's own action buttons wrapped into ragged
                rows of 28px-tall controls on a phone. `contents` means this
                wrapper generates no box at >= 768px, so the desktop header is
                byte-for-byte the same layout it was. */}
            <div className="contents max-md:flex max-md:w-full max-md:flex-wrap max-md:gap-2 max-md:[&>button]:min-h-[44px] max-md:[&>button]:flex-1 max-md:[&>button]:justify-center">
              {children}
            </div>
            
            {/* Search / command palette - replaces the Quick Action menu (press / or Ctrl+K; Q still works) */}
            <button
              type="button"
              ref={quickActionsRef}
              onClick={() => openCommandPalette()}
              className="flex items-center gap-2 px-3 py-2 bg-white hover:bg-slate-50 border border-slate-300 text-slate-600 rounded-lg transition-colors text-sm shadow-sm dark:bg-slate-900 dark:border-slate-700 dark:text-slate-300 max-md:h-[44px] max-md:min-w-[44px] max-md:justify-center"
              data-testid="command-palette-btn"
              title="Search everything (press / or Ctrl+K)"
            >
              <Search className="w-4 h-4" />
              <span className="hidden sm:inline">Search</span>
              <kbd className="hidden md:inline rounded border border-slate-200 bg-slate-50 px-1.5 text-[10px] leading-4 text-slate-500 dark:border-slate-700 dark:bg-slate-800">/</kbd>
              <kbd className="hidden lg:inline rounded border border-slate-200 bg-slate-50 px-1.5 text-[10px] leading-4 text-slate-500 dark:border-slate-700 dark:bg-slate-800">⌘K</kbd>
            </button>

            {/* Theme Toggle */}
            <button
              onClick={cycleTheme}
              className="relative p-2 text-slate-500 hover:text-slate-700 hover:bg-slate-100 dark:text-slate-400 dark:hover:text-slate-200 dark:hover:bg-slate-800 rounded-lg transition-colors max-md:h-[44px] max-md:w-[44px] max-md:inline-flex max-md:items-center max-md:justify-center"
              data-testid="theme-toggle-btn"
              title={`Theme: ${theme === 'system' ? 'System' : theme === 'dark' ? 'Dark' : 'Light'}`}
            >
              {theme === 'system' ? (
                <Monitor className="w-5 h-5" />
              ) : theme === 'dark' ? (
                <Moon className="w-5 h-5" />
              ) : (
                <Sun className="w-5 h-5" />
              )}
            </button>
            
            {/* Contextual Help Button */}
            <button
              onClick={() => {
                // Navigate to help with current page context
                const currentPath = location.pathname;
                navigate(`/ats/help?context=${encodeURIComponent(currentPath)}`);
              }}
              className="p-2 text-slate-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors max-md:h-[44px] max-md:w-[44px] max-md:inline-flex max-md:items-center max-md:justify-center"
              title="Help (contextual)"
              data-testid="contextual-help-btn"
            >
              <HelpCircle className="w-5 h-5" />
            </button>
            
            {/* mailbox-v3: chime + bottom-right mail toasts on every page */}
            <MailNotifyBridge onNotification={() => loadNotifications()} />
            {/* Notifications Bell */}
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setShowDropdown(!showDropdown)}
                className="relative p-2 text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-colors max-md:h-[44px] max-md:w-[44px] max-md:inline-flex max-md:items-center max-md:justify-center"
                data-testid="notifications-btn"
              >
                <Bell className="w-5 h-5" />
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </button>

              {showDropdown && (
                <NotificationPanelShell anchorRef={dropdownRef} onClose={() => setShowDropdown(false)}>
                  <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
                    <h3 className="font-semibold text-slate-900">Notifications</h3>
                    {unreadCount > 0 && (
                      <button
                        onClick={handleMarkAllRead}
                        disabled={loading}
                        className="text-xs text-blue-600 hover:text-blue-700 flex items-center gap-1"
                      >
                        {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCheck className="w-3 h-3" />}
                        Mark all read
                      </button>
                    )}
                  </div>
                  <div className="flex-1 min-h-0 overflow-y-auto scrollbar-thin">
                    {notifications.length === 0 ? (
                      <div className="px-4 py-8 text-center text-sm text-slate-500">
                        No notifications yet
                      </div>
                    ) : (
                      notifications.map((notification) => (
                        <div
                          key={notification.id}
                          className={cn(
                            "flex items-start gap-3 px-4 py-3 border-b border-slate-50 hover:bg-slate-50 transition-colors cursor-pointer",
                            !notification.read && "bg-blue-50/50"
                          )}
                          onClick={() => handleNotificationClick(notification)}
                        >
                          <div className="mt-0.5">{getNotificationIcon(notification.type)}</div>
                          <div className="flex-1 min-w-0">
                            <p className={cn("text-sm", notification.read ? "text-slate-600" : "text-slate-900 font-medium")}>
                              {safeText(notification.title)}
                            </p>
                            {notification.entity_type === 'mail'
                              ? <MailNotificationRow n={notification} compact onSnoozed={() => loadNotifications()} />
                              : <p className="text-xs text-slate-500 truncate mt-0.5">{previewText(notification.message)}</p>}
                            <p className="text-xs text-slate-400 mt-1">{formatTime(notification.created_at)}</p>
                          </div>
                          {!notification.read && <div className="w-2 h-2 bg-blue-500 rounded-full mt-2" />}
                        </div>
                      ))
                    )}
                  </div>
                  {notifications.length > 0 && (
                    <div className="px-4 py-2 border-t border-slate-100 bg-slate-50">
                      <p className="text-xs text-center text-slate-500">Showing last {notifications.length} notifications</p>
                    </div>
                  )}
                </NotificationPanelShell>
              )}
            </div>
            
            {/* Marquee Box (replaces System Admin text) */}
            <div 
              className="hidden md:flex items-center gap-2 px-3 py-1 bg-white border border-slate-300 rounded-md cursor-pointer hover:bg-slate-50 transition-all min-w-[160px] max-w-[240px] h-8"
              onClick={openMarqueeModal}
              title="Click to customize quotes"
              data-testid="marquee-box"
            >
              <div className="flex-1 overflow-hidden h-5 relative">
                <div className="flip-up-container">
                  <p 
                    className={`text-slate-800 text-sm leading-5 whitespace-nowrap ${fontStyleClasses[marqueeSettings.fontStyle] || ''}`}
                    style={{ fontSize: `${Math.min(marqueeSettings.fontSize, 14)}px` }}
                    key={currentQuoteIndex}
                  >
                    {marqueeQuotes[currentQuoteIndex]}
                  </p>
                </div>
              </div>
              <Settings2 className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
            </div>
          </div>
        </div>
      </header>

      {/* Marquee Settings Modal */}
      <Dialog open={marqueeModalOpen} onOpenChange={setMarqueeModalOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Quote className="w-5 h-5 text-purple-600" />
              Customize Your Quotes
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <p className="text-sm text-slate-500">Add up to 6 quotes that will rotate in the header box.</p>
            
            {[0, 1, 2, 3, 4, 5].map((i) => (
              <div key={i}>
                <Label>Quote {i + 1}</Label>
                <Input
                  value={editQuotes[i] || ''}
                  onChange={(e) => {
                    const newQuotes = [...editQuotes];
                    newQuotes[i] = e.target.value;
                    setEditQuotes(newQuotes);
                  }}
                  placeholder={`Enter quote ${i + 1}...`}
                />
              </div>
            ))}

            <div className="pt-4 border-t space-y-4">
              <div>
                <Label>Rotation Speed: {marqueeSettings.speed}%</Label>
                <Slider
                  value={[marqueeSettings.speed]}
                  onValueChange={([v]) => setMarqueeSettings(prev => ({ ...prev, speed: v }))}
                  min={10}
                  max={100}
                  step={5}
                  className="mt-2"
                />
                <p className="text-xs text-slate-400 mt-1">Higher = faster rotation between quotes</p>
              </div>

              <div>
                <Label>Font Size: {marqueeSettings.fontSize}px</Label>
                <Slider
                  value={[marqueeSettings.fontSize]}
                  onValueChange={([v]) => setMarqueeSettings(prev => ({ ...prev, fontSize: v }))}
                  min={12}
                  max={20}
                  step={1}
                  className="mt-2"
                />
              </div>

              <div>
                <Label>Font Style</Label>
                <Select 
                  value={marqueeSettings.fontStyle} 
                  onValueChange={(v) => setMarqueeSettings(prev => ({ ...prev, fontStyle: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="normal">Normal</SelectItem>
                    <SelectItem value="italic">Italic</SelectItem>
                    <SelectItem value="bold">Bold</SelectItem>
                    <SelectItem value="bold-italic">Bold Italic</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setMarqueeModalOpen(false)}>Cancel</Button>
              <Button onClick={saveMarqueeSettings}>Save Changes</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default Header;
